// istanbul ignore file
import React from 'react';
import { createRoot } from 'react-dom/client';
import { createStore, combineReducers, applyMiddleware, compose } from 'redux';
import { Provider } from 'react-redux';
import thunkMiddleware from 'redux-thunk';
import { BrowserRouter } from 'react-router-dom';
import Modal from 'react-modal';

import { persistStore, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage'; 
import { PersistGate } from 'redux-persist/integration/react';

import config from './config';
import reducers from './reducers';
import api from './api';

import App from './containers/App';
import register from './registerServiceWorker';
import ScrollToTop from './components/ScrollToTop';
import ErrorBoundary from './components/ErrorBoundary';

import './index.scss';

Modal.setAppElement('#root');

const apiBlacklist = Object.keys(api.reducers).filter(key => key !== 'auth' && key !== 'profile');

const persistConfig = {
  key: 'chez-nicu',
  storage,
  blacklist: [
    'router',
    'ui',
    'questions',
    'visitationRecords',
    'growthRecords',
    'comfortRecords',
    'bathingRecords',
    'form', 
    'content',
    ...apiBlacklist 
  ]
};

const appReducer = combineReducers({
  ...reducers,
  ...api.reducers
});

const rootReducer = persistReducer(persistConfig, appReducer);

const composeEnhancers =
  (import.meta.env.DEV &&
    window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__) ||
  compose;

const reduxApiMiddleware = () => next => action => {
  const result = next(action);
  if (result && typeof result.catch === 'function') {
    return result.catch(err => {
      if (err === 'isLoading') return;
      return Promise.reject(err);
    });
  }
  return result;
};

const store = createStore(
  rootReducer,
  composeEnhancers(
    applyMiddleware(reduxApiMiddleware, thunkMiddleware)
  )
);

const persistor = persistStore(store);

const root = createRoot(document.getElementById('root'));
root.render(
  <Provider store={store}>
    <PersistGate loading={null} persistor={persistor}>
      <BrowserRouter>
        <ErrorBoundary>
          <ScrollToTop>
            <App />
          </ScrollToTop>
        </ErrorBoundary>
      </BrowserRouter>
    </PersistGate>
  </Provider>
);

register();